import { buildMolBridge } from './molBridge';
import { buildKartuizersDeck } from './kartuizersLandings';
import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import type { BridgeDeck } from '../../site/types';
import type { PaletteModel } from '../../data/types';
import type { LinearRGB } from '../../core/color';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { ChunkSink } from '../mesh/chunker';
import { SURFACE, edgeIdFor, type SurfaceKind } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import { WORLD } from '../../core/config';
import { distancePointSegment, ensureCCW, ensureCW, pointInPolygon } from '../../geo/polygon';
import { triangulate } from '../../geo/triangulate';
import { buildParapets } from './bridgeParapets';
import { RAILING, buildRailing } from './bridgeRailing';
import { bridgeProfile, TIMBER_BRIDGE } from '../../site/bridgeHeight';
import { intersection, fromRing } from '../../geo/clip';
import { beamSolid } from '../fabric/solids';
import { buildNieuweBrug, buildNieuweBrugParapets } from './nieuweBrug';
import { buildMasonryBridge } from './masonryBridges';
import { buildBridgeSolid } from './bridgeSolid';

// Walkers stand on a deck at ground level (PLAN §7), so the deck prism hangs from there.
const DECK_TOP = WORLD.groundY;
const DECK_BOTTOM = WORLD.groundY - 0.35;
const STONE_ARCH_BOTTOM = -1.2;
const PILE = { bottom: -2.5, radius: 0.125, segments: 6, spacing: 4, across: 0.7 } as const;
const LIFT = { height: 6, along: 0.4, depth: 0.4, beam: 0.5, beamShare: 0.3 } as const;
// Clipper's 1 mm grid leaves a deck's sides this close to the bridge's width lines.
const SIDE_TOLERANCE_M = 0.01;
const EDGE_ID_BASE = 20000;
const PLAN_FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };

/** A deck edge along one side of the bridge, run with the deck on its left. */
interface Side {
  readonly a: Vec2;
  readonly b: Vec2;
}

interface BridgePlan {
  readonly bridge: BridgeDeck;
  readonly middle: Vec2;
  readonly frame: Frame;
  readonly decks: readonly PolygonWithHoles[];
  readonly sides: readonly Side[];
  readonly span: readonly [number, number];
}

export function buildBridges(
  bridges: readonly BridgeDeck[],
  palette: PaletteModel,
  chunks: ChunkSink
): { readonly count: number } {
  bridges.forEach((bridge, index) => {
    buildBridge(planOf(bridge), index, palette, chunks);
  });
  return { count: bridges.length };
}

function planOf(bridge: BridgeDeck): BridgePlan {
  const middle: Vec2 = [(bridge.a[0] + bridge.b[0]) / 2, (bridge.a[1] + bridge.b[1]) / 2];
  const yaw = Math.atan2(bridge.b[1] - bridge.a[1], bridge.b[0] - bridge.a[0]);
  const frame: Frame = { origin: layoutToWorld(middle, 0), yaw };
  const decks = bridge.deck.map((deck) => ({
    outer: ensureCCW(deck.outer),
    holes: deck.holes.map((hole) => ensureCW(hole)),
  }));
  const along = decks.flatMap((deck) => deck.outer.map((p) => localOf(frame, p)[0]));
  return {
    bridge,
    middle,
    frame,
    decks,
    sides: sidesOf(decks, frame, bridge.width / 2),
    span: [Math.min(...along), Math.max(...along)],
  };
}

// The builder turns a frame's local x along its yaw and local z to the right of it.
function localOf(frame: Frame, p: Vec2): Vec2 {
  const [dx, dy] = [p[0] - frame.origin[0], p[1] + frame.origin[2]];
  const [cos, sin] = [Math.cos(frame.yaw), Math.sin(frame.yaw)];
  return [dx * cos + dy * sin, dx * sin - dy * cos];
}

function layoutOf(frame: Frame, along: number, across: number): Vec2 {
  const [cos, sin] = [Math.cos(frame.yaw), Math.sin(frame.yaw)];
  return [
    frame.origin[0] + along * cos + across * sin,
    -frame.origin[2] + along * sin - across * cos,
  ];
}

function sidesOf(decks: readonly PolygonWithHoles[], frame: Frame, half: number): Side[] {
  const onSide = (across: number) => Math.abs(Math.abs(across) - half) <= SIDE_TOLERANCE_M;
  return decks.flatMap(({ outer }) =>
    outer.flatMap((a, i) => {
      const b = outer[(i + 1) % outer.length]!;
      const [from, to] = [localOf(frame, a)[1], localOf(frame, b)[1]];
      return onSide(from) && onSide(to) && Math.sign(from) === Math.sign(to) ? [{ a, b }] : [];
    })
  );
}

// aMeta.z carries the surface's pattern preset, and no bridge has one yet.
function styleOf(color: LinearRGB, surface: SurfaceKind, index: number): VertexStyle {
  return {
    color,
    surface,
    edgeId: edgeIdFor(index + EDGE_ID_BASE),
    seed: 0,
    strokeAngle: 0,
    extent: [0, 0],
  };
}

function buildBridge(
  plan: BridgePlan,
  index: number,
  palette: PaletteModel,
  chunks: ChunkSink
): void {
  const builder = chunks.builderAt('infrastructure', plan.middle);
  const stone = styleOf(palette.towerStone.median.linear, SURFACE.stone, index);
  const wood = styleOf(palette.shipHull.median.linear, SURFACE.wood, index);
  if (plan.bridge.id === 'bridge-nieuwe-brug') {
    buildNieuweBrug(builder, stone, wood, plan.bridge);
    buildNieuweBrugParapets(builder, stone, wood, plan.bridge, plan.sides);
    return;
  }
  const profile = bridgeProfile(plan.bridge);
  const deckStyle: VertexStyle = {
    ...(plan.bridge.kind === 'stone-arch' ? stone : wood),
    strokeAngle: plan.frame.yaw,
  };
  const stoneArch = plan.bridge.kind === 'stone-arch';
  if (buildMolBridge(builder, stone, plan.bridge)) return;
  if (buildMasonryBridge(builder, stone, plan.bridge, plan.sides)) return;
  // A stone bridge is solid masonry from the deck down to its arch bottom.
  const bottom = stoneArch ? STONE_ARCH_BOTTOM : DECK_BOTTOM;
  if (!buildKartuizersDeck(builder, deckStyle, plan.bridge))
    plan.decks.forEach((deck) => {
      if (profile.raised) buildRaisedDeck(builder, deckStyle, deck, plan);
      else
        buildBridgeSolid(
          builder,
          deckStyle,
          deck,
          () => bottom,
          () => DECK_TOP
        );
    });
  // A gate's walls flank its bridge and the gate lifts it (AAR 95 p. 11), so the deck stands alone.
  if (plan.bridge.gate !== undefined) return;
  if (stoneArch) {
    buildParapets(builder, stone, plan.sides, profile);
    return;
  }
  // The Kolk's masonry vault supports its deck; keep the bank guards but no obstructing piles.
  if (plan.bridge.id !== 'bridge-way-38316242') {
    buildPiles(builder, wood, plan);
    if (profile.raised) buildBents(builder, wood, plan);
  }
  plan.sides.forEach((side) => {
    const start = profile.along(side.a);
    const direction = profile.along(side.b) >= start ? 1 : -1;
    buildRailing(
      builder,
      wood,
      sideFrame(side),
      lengthOf(side),
      (x) => profile.height(start + direction * x) - WORLD.groundY,
      profile.raised ? profile.knots.map((x) => (x - start) * direction) : []
    );
  });
  if (plan.bridge.kind === 'draw') buildLift(builder, wood, plan);
}

/** Split the deck at each approach break, retaining only its exterior walls. */
function buildRaisedDeck(
  builder: MeshBuilder,
  style: VertexStyle,
  deck: PolygonWithHoles,
  plan: BridgePlan
): void {
  const p = bridgeProfile(plan.bridge);
  const at = (x: number, z: number): Vec2 => [
    plan.bridge.a[0] + p.axis[0] * x - p.axis[1] * z,
    plan.bridge.a[1] + p.axis[1] * x + p.axis[0] * z,
  ];
  const reach = plan.bridge.width + 2;
  for (let k = 1; k < p.knots.length; k++) {
    const a = p.knots[k - 1]!;
    const b = p.knots[k]!;
    const slab = fromRing([at(a, -reach), at(b, -reach), at(b, reach), at(a, reach)]);
    for (const piece of intersection([deck], [slab])) {
      const t = triangulate(piece);
      const point = (v: number): Vec2 => [t.positions[2 * v]!, t.positions[2 * v + 1]!];
      for (let j = 0; j < t.indices.length; j += 3) {
        const tri = upward(
          point(t.indices[j]!),
          point(t.indices[j + 1]!),
          point(t.indices[j + 2]!)
        );
        builder.polygon(
          style,
          PLAN_FRAME,
          tri.map((q) => layoutToWorld(q, p.height(p.along(q))))
        );
        builder.polygon(
          style,
          PLAN_FRAME,
          [...tri]
            .reverse()
            .map((q) =>
              layoutToWorld(
                q,
                plan.bridge.kind === 'stone-arch'
                  ? STONE_ARCH_BOTTOM
                  : p.height(p.along(q)) - TIMBER_BRIDGE.thickness
              )
            )
        );
      }
      for (const ring of [piece.outer, ...piece.holes]) {
        ring.forEach((q, i) => {
          const r = ring[(i + 1) % ring.length]!;
          const mid: Vec2 = [(q[0] + r[0]) / 2, (q[1] + r[1]) / 2];
          const exterior = [deck.outer, ...deck.holes].some((boundary) =>
            boundary.some(
              (a, j) =>
                distancePointSegment(mid, { a, b: boundary[(j + 1) % boundary.length]! }) < 0.002
            )
          );
          if (!exterior) return;
          const yq = p.height(p.along(q)),
            yr = p.height(p.along(r));
          const bq =
            plan.bridge.kind === 'stone-arch' ? STONE_ARCH_BOTTOM : yq - TIMBER_BRIDGE.thickness;
          const br =
            plan.bridge.kind === 'stone-arch' ? STONE_ARCH_BOTTOM : yr - TIMBER_BRIDGE.thickness;
          builder.quad(style, PLAN_FRAME, [
            layoutToWorld(q, bq),
            layoutToWorld(r, br),
            layoutToWorld(r, yr),
            layoutToWorld(q, yq),
          ]);
        });
      }
    }
  }
}

/** Caps, stringers and diagonal wind braces make the long crossing a timber structure. */
function buildBents(builder: MeshBuilder, wood: VertexStyle, plan: BridgePlan): void {
  const p = bridgeProfile(plan.bridge);
  const spots = pileSpots(plan);
  const half = (PILE.across * plan.bridge.width) / 2;
  // A skew bank can leave only one pile of a row under the deck. Its cross-cap
  // needs both ends covered; otherwise it projects out through the landing.
  const rows = spots
    .filter(([, z]) => z < 0)
    .filter(([x]) =>
      [-half - 0.15, half + 0.15].every((z) => covers(plan.decks, layoutOf(plan.frame, x, z), 0.07))
    );
  const height = (x: number) =>
    p.height(p.along(layoutOf(plan.frame, x, 0))) - TIMBER_BRIDGE.thickness;
  rows.forEach(([x]) => {
    const h = height(x);
    beamSolid(
      builder,
      wood,
      plan.frame,
      [x, h - 0.16, -half - 0.15],
      [x, h - 0.16, half + 0.15],
      0.14
    );
    if (h > 0.8) {
      beamSolid(
        builder,
        wood,
        plan.frame,
        [x, WORLD.waterY - 0.2, -half],
        [x, h - 0.3, half],
        0.09
      );
      beamSolid(
        builder,
        wood,
        plan.frame,
        [x + 0.2, WORLD.waterY - 0.2, half],
        [x + 0.2, h - 0.3, -half],
        0.09
      );
    }
  });
  const fairway = fairwayBay(rows.map(([x]) => x));
  for (let i = 1; i < rows.length; i++) {
    const a = rows[i - 1]![0],
      b = rows[i]![0];
    for (const z of [-half, half]) {
      beamSolid(
        builder,
        wood,
        plan.frame,
        [a, height(a) - 0.12, z],
        [b, height(b) - 0.12, z],
        0.12
      );
      if (i !== fairway && height(a) > 0.8 && height(b) > 0.8) {
        beamSolid(
          builder,
          wood,
          plan.frame,
          [a, WORLD.waterY - 0.1, z],
          [b, height(b) - 0.3, z],
          0.08
        );
      }
    }
  }
}

/* The bay over the middle of the water carries no stay down to the waterline. A stay there leaves
   no bay a rowing boat and its crew can pass, and the Damrak's lighters reached the Dam through
   these crossings. Which bay stood open is an estimate; the sources draw stays, not their bays.
   The index is that of the row closing the bay. */
function fairwayBay(rows: readonly number[]): number {
  const middle = (rows[0]! + rows[rows.length - 1]!) / 2;
  let nearest = -1;
  for (let i = 1; i < rows.length; i++) {
    const offset = (bay: number) => Math.abs((rows[bay - 1]! + rows[bay]!) / 2 - middle);
    if (nearest < 0 || offset(i) < offset(nearest)) nearest = i;
  }
  return nearest;
}

// Counter-clockwise on the plan is up-facing, because layout y runs along world −z.
function upward(a: Vec2, b: Vec2, c: Vec2): readonly [Vec2, Vec2, Vec2] {
  const area = (b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1]);
  return area >= 0 ? [a, b, c] : [a, c, b];
}

// A side's frame has local z pointing off the deck, so barriers stand just outside it.
function sideFrame({ a, b }: Side): Frame {
  return { origin: layoutToWorld(a, DECK_TOP), yaw: Math.atan2(b[1] - a[1], b[0] - a[0]) };
}

function lengthOf({ a, b }: Side): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function covers(decks: readonly PolygonWithHoles[], p: Vec2, margin: number): boolean {
  return decks.some(
    (deck) =>
      pointInPolygon(p, deck) &&
      [deck.outer, ...deck.holes].every((ring) =>
        ring.every(
          (a, i) => distancePointSegment(p, { a, b: ring[(i + 1) % ring.length]! }) >= margin
        )
      )
  );
}

// Bents stand mid-bay at most PILE.spacing apart, where the deck covers the whole pile.
function pileSpots(plan: BridgePlan): Vec2[] {
  const [from, to] = plan.span;
  const bents = Math.max(1, Math.ceil((to - from) / PILE.spacing));
  const across = (PILE.across * plan.bridge.width) / 2;
  return Array.from({ length: bents }, (_, k) => from + ((k + 0.5) * (to - from)) / bents)
    .flatMap((along): Vec2[] => [
      [along, -across],
      [along, across],
    ])
    .filter(([along, off]) => covers(plan.decks, layoutOf(plan.frame, along, off), PILE.radius));
}

function buildPiles(builder: MeshBuilder, style: VertexStyle, plan: BridgePlan): void {
  pileSpots(plan).forEach(([along, across]) => {
    builder.cylinder(
      style,
      plan.frame,
      [along, PILE.bottom, across],
      PILE.radius,
      PILE.radius,
      (bridgeProfile(plan.bridge).raised
        ? bridgeProfile(plan.bridge).height(
            bridgeProfile(plan.bridge).along(layoutOf(plan.frame, along, across))
          ) - TIMBER_BRIDGE.thickness
        : DECK_BOTTOM) - PILE.bottom,
      PILE.segments
    );
  });
}

// The uprights stand against the rails' outer faces and carry the beam across the deck.
function buildLift(builder: MeshBuilder, style: VertexStyle, plan: BridgePlan): void {
  const [from, to] = plan.span;
  const middle = (from + to) / 2;
  const centre = layoutOf(plan.frame, middle, 0);
  const profile = bridgeProfile(plan.bridge);
  const level = profile.height(profile.along(centre));
  plan.sides.forEach((side) => {
    const length = lengthOf(side);
    const at =
      ((centre[0] - side.a[0]) * (side.b[0] - side.a[0]) +
        (centre[1] - side.a[1]) * (side.b[1] - side.a[1])) /
      length;
    if (at < LIFT.along / 2 || at > length - LIFT.along / 2) return;
    builder.box(
      style,
      sideFrame(side),
      [at - LIFT.along / 2, level - DECK_TOP, RAILING.thickness],
      [at + LIFT.along / 2, level - DECK_TOP + LIFT.height, RAILING.thickness + LIFT.depth]
    );
  });
  const reach = plan.bridge.width / 2 + RAILING.thickness + LIFT.depth;
  const half = (LIFT.beamShare * (to - from)) / 2;
  builder.box(
    style,
    plan.frame,
    [middle - half, level + LIFT.height, -reach],
    [middle + half, level + LIFT.height + LIFT.beam, reach]
  );
}
